import React from "react";
import { ChangeEvent, FormEvent } from "react";
import ReactModal from "react-modal";
import { Prompt, RouteComponentProps } from "react-router";

import { V2 } from "../../Api";
import withTitle from "../withTitle";

import OverflowMenuItem from "../OverflowMenuItem";
import ToolbarMenuItem from "../ToolbarMenuItem";
import TreeView from "../TreeView";
import { ITreeViewItem, TreeViewEvent } from "../TreeViewItem";
import ValidateInput from "../ValidateInput";
import ViewToolbar from "../ViewToolbar";

import "../../blocks.css";
import "../../modal.css";
import "../../schedule.css";
import "../viewer.css";
import Checkbox from "../Checkbox";

interface ISurgeryTypeCreationViewState {
    changedFields: string[];
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean | string;
    isModalOpen: boolean;
    submitStatus: boolean | string;

    isMajor: boolean;
    name: string;
    shortName: string;
    options: any[];
    cpt: string;

    instructions: any[];

    specimens: any[];

    [name: string]: any;
}

class SurgeryTypeCreationView extends React.Component<RouteComponentProps<any>, ISurgeryTypeCreationViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        this.state = {
            changedFields: [],
            hasChanged: false,
            isEdit: props.match.params.id !== undefined,
            isLoading: !!this.props.match.params.id,
            isModalOpen: false,
            submitStatus: false,

            isMajor: false,
            name: "",
            options: [],
            shortName: "",
            cpt: "",

            instructions: [],

            specimens: [],
        };

        this.inputs = [];

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.handleItemAdd = this.handleItemAdd.bind(this);
        this.handleItemRemove = this.handleItemRemove.bind(this);
        this.handleItemUpdate = this.handleItemUpdate.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.doRealDelete = this.doRealDelete.bind(this);
        this.closeDialog = this.closeDialog.bind(this);
        this.moveInstructionUp = this.moveInstructionUp.bind(this);
        this.moveInstructionDown = this.moveInstructionDown.bind(this);
        this.addInstruction = this.addInstruction.bind(this);
    }

    public async componentDidMount() {
        if (!this.props.match.params.id) {
            return;
        }

        try {
            const [surgery, specimens] = await Promise.all([
                V2.get<any>(`glossary/surgeries/${this.props.match.params.id}`),
                V2.get<any>(`glossary/specimens`),
            ]);

            this.setState({
                isLoading: false,

                isMajor: surgery.isMajor,
                name: surgery.name,
                options: surgery.options,
                shortName: surgery.shortName,
                cpt: surgery.cpt,
                instructions: surgery.instructions,

                specimens
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (typeof this.state.isLoading === "string") {
            return <div className="error-box">{this.state.isLoading}</div>;
        }

        if (this.state.isLoading) {
            return <div className="spinner spinner-center lds-dual-ring-big" />
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem condition={this.state.isEdit}>
                            <button type="button" onClick={this.handleDelete}><i className="icon-trash" /><span>Delete Surgery Type</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ReactModal
                    isOpen={this.state.isModalOpen} onRequestClose={this.closeDialog}
                    overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Confirm Delete">
                    <form className="input-form" onSubmit={this.doRealDelete}>
                        <section className="info-block">
                            <header>
                                <h3>Confirm Delete</h3>
                            </header>

                            <span>Are you sure you want to delete this surgery type?</span>
                            <p>{this.state.shortName} ({this.state.name})</p>
                            <div className="action-panel-dialog">
                                <button type="submit" className="button red push-right">Yes, Delete</button>
                                <button type="button" className="button" onClick={this.closeDialog}>No</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Basic Information</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Full Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="name" value={this.state.name} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-short" className="required">Short Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name-short" name="shortName" value={this.state.shortName} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>

                            <section>
                                <div className="input-form-group">
                                    <div className="input-form-group-check-inline checkbox">
                                        <Checkbox fancy={true} id="frm-is-major" name="isMajor" checked={this.state.isMajor} onChange={this.formInputChanged} autoComplete="off_please_chrome">Is normally major surgery</Checkbox>
                                    </div>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-cpt" className="required">CPT Code</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-cpt" name="cpt" value={this.state.cpt} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Options</h3>
                            </header>

                            <TreeView name="options" items={this.state.options} onNewItem={this.handleItemAdd} onRemoveItem={this.handleItemRemove} onUpdateItem={this.handleItemUpdate} itemNameRender={this.renderOptionName} />
                        </section>
                    </div>
                    &nbsp;
                    <section className="info-block">
                        <header>
                            <h3>CPT Override</h3>
                        </header>
                        <p>
                            This section allows conditional overriding of the resulting CPT code based on properties of the containing surgery.<br />
                            Rows are evaluated top down, first Result match wins.  If no row matches, no override is used and default logic applies.
                        </p>
                        <p>
                            <small>Note: When these rules are changed, any existing overridden CPT codes are <em>NOT</em> updated to reflect the new rules.  Only surgeries changed afterwards will use the new rules.</small>
                        </p>
                        <table className="table-vertical">
                            <thead>
                                <tr>
                                    <th>Flag</th>
                                    <th>Dataset</th>
                                    <th>Filter</th>
                                    <th>Property</th>
                                    <th>Compare</th>
                                    <th>Value</th>
                                    <th>Function</th>
                                    <th>Parameter</th>
                                    <th className="col-smallest" />
                                    <th className="col-smallest" />
                                    <th className="col-smallest" />
                                    <th className="col-smallest" />
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    this.state.instructions.map((a, i, arr) => (
                                        a.isEdit
                                            ? <tr className="edit-row" key={a.row}>
                                                <td className="col-smallest col-nopad">
                                                    <input type="text" name="flag" value={a.flag || ""} onChange={e => this.handleRowChange(e, i)} style={{ minWidth: "7em" }} />
                                                </td>
                                                <td className="col-smallest col-nopad">
                                                    <select name="dataSet" value={a.dataSet} onChange={e => this.handleRowChange(e, i)} style={{ minWidth: "6em" }}>
                                                        <option />
                                                        <option value="specimen">Specimens</option>
                                                        <option value="option">Options</option>
                                                    </select>
                                                </td>
                                                <td className="col-nopad">
                                                    {
                                                        a.dataSet === "specimen"
                                                            ? <select name="filter" value={a.filter} onChange={e => this.handleRowChange(e, i)}>
                                                                {this.state.specimens.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                                            </select>
                                                            : a.dataSet === "option"
                                                                ? <select name="filterString" value={a.filterString} onChange={e => this.handleRowChange(e, i)}>
                                                                    {this.state.options.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                                                </select>
                                                                : <input type="text" name="filter" value={a.filterString || ""} onChange={e => this.handleRowChange(e, i)} />
                                                    }
                                                </td>
                                                <td className="col-smallest col-nopad">
                                                    <select name="select" value={a.select || ""} onChange={e => this.handleRowChange(e, i)}>
                                                        <option />
                                                        <option value="size">Size</option>
                                                        <option value="weight">Weight</option>
                                                    </select>
                                                </td>
                                                <td className="col-smallest col-nopad">
                                                    <select name="compareOperation" value={a.compareOperation || ""} onChange={e => this.handleRowChange(e, i)} style={{ minWidth: "8em" }}>
                                                        <option />
                                                        <option value="0">Equal</option>
                                                        <option value="1">Not equal</option>
                                                        <option value="2">Greater than</option>
                                                        <option value="3">Greater or equal</option>
                                                        <option value="4">Less than</option>
                                                        <option value="5">Less or equal</option>
                                                    </select>
                                                </td>
                                                <td className="col-smallest col-nopad">
                                                    <input type="number" name="compareValue" value={a.compareValue || a.compareString || ""} onChange={e => this.handleRowChange(e, i)} />
                                                </td>
                                                <td className="col-smallest col-nopad">
                                                    <select name="function" value={a.function} onChange={e => this.handleRowChange(e, i)} style={{ minWidth: "5.5em" }}>
                                                        <option />
                                                        <option value="0">Result</option>
                                                        <option value="1">Set flag</option>
                                                        <option value="2">Clear flag</option>
                                                    </select></td>
                                                <td className="col-nopad">
                                                    <input type="text" name="functionParameter" value={a.functionParameter || ""} onChange={e => this.handleRowChange(e, i)} style={{ minWidth: "7em" }} />
                                                </td>
                                                <td colSpan={4} className="col-nopad">
                                                    <button type="button" className="button smaller btn-light text-with-icon" title="Save" style={{ width: "100%" }} onClick={() => this.toggleEdit(i)}><i className="icon-check" /><span>Done</span></button>
                                                </td>
                                            </tr>
                                            : <tr key={a.row}>
                                                <td className="col-smallest">{a.flag}</td>
                                                <td className="col-smallest">{a.dataSet}</td>
                                                <td className="wrap">
                                                    {
                                                        a.dataSet === "specimen"
                                                            ? (this.state.specimens.find(b => b.id === a.filter) || {}).name || a.filter
                                                            : a.dataSet === "option"
                                                                ? (this.state.options.find(b => b.id === a.filterString) || {}).name || a.filterString
                                                                : (a.filter || a.filterString)
                                                    }
                                                </td>
                                                <td className="col-smallest">{a.select}</td>
                                                <td className="col-smallest">{this.comparisonToString(a.compareOperation)}</td>
                                                <td className="col-smallest">{a.compareValue || a.compareString}</td>
                                                <td className="col-smallest">{this.functionToString(a.function)}</td>
                                                <td>{a.functionParameter}</td>
                                                <td className="col-nopad">
                                                    <button type="button" className="button smaller btn-light" title="Edit" onClick={() => this.toggleEdit(i)}><i className="icon-pencil" /></button>
                                                </td>
                                                <td className="col-nopad">
                                                    <button type="button" className="button smaller btn-light" title="Move up" disabled={i === 0} onClick={() => this.moveInstructionUp(i)}><i className="icon-chevron-up" /></button>
                                                </td>
                                                <td className="col-nopad">
                                                    <button type="button" className="button smaller btn-light" title="Move down" disabled={i === arr.length - 1} onClick={() => this.moveInstructionDown(i)}><i className="icon-chevron-down" /></button>
                                                </td>
                                                <td className="col-nopad">
                                                    <button type="button" className="button smaller btn-light red" title="Delete" onClick={() => this.deleteRow(i)}><i className="icon-times" /></button>
                                                </td>
                                            </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                        &nbsp;
                        <div>
                            <button type="button" className="button btn-light push-right text-with-icon" onClick={this.addInstruction}><i className="icon-plus" /><span>Add</span></button>
                        </div>
                    </section>
                    &nbsp;
                    {
                        this.state.submitStatus === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.submitStatus === "string"
                                ? <span className="error-text">{this.state.submitStatus}</span>
                                : ""
                    }
                    <div className="action-panel">
                        <button type="submit" className="button push-right" onClick={this.submitForm} disabled={this.state.submitStatus === true}>Submit</button>
                        <button type="button" className="button" onClick={this.cancelForm} disabled={this.state.submitStatus === true}>Cancel</button>
                    </div>
                </form>

            </div>
        );
    }

    private receiveValidationRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        const instructions = this.state.instructions;
        instructions.forEach((a, i) => a.row = i);

        const data = {
            isMajor: this.state.isMajor,
            name: this.state.name,
            options: this.state.options,
            shortName: this.state.shortName,
            cpt: this.state.cpt,

            instructions
        };

        try {
            this.setState({
                submitStatus: true
            });

            if (this.state.isEdit) {
                await V2.patch(`glossary/surgeries/${this.props.match.params.id}`, data);
            }
            else {
                await V2.post("glossary/surgeries", data);
            }

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, callback?: (state: Partial<ISurgeryTypeCreationViewState>) => void) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState(s => {
            if (!s.changedFields.some(a => a === name)) {
                s.changedFields.push(name);
            }

            const newState = {
                changedFields: s.changedFields,
                hasChanged: true,
                [name]: value
            }

            if (callback) {
                callback(newState);
            }

            return newState;
        });
    }

    // private itemRender(item: any) {
    //    return item.name || `ERROR: Invalid name ${item}`;
    // }

    private handleItemAdd(e: TreeViewEvent) {
        if (!e.value) {
            return;
        }

        const item = {
            fullName: e.value,
            id: e.value.toLowerCase().replace(/\s/g, "_"),
            name: e.value,
            shortName: e.shortValue,
            cpt: e.extra.cpt || undefined
        };

        this.setState(s => {
            if (e.parent) {
                if (!e.parent.children) {
                    e.parent.children = [];
                }

                item.id = e.parent.id + "_" + item.id;
                item.fullName = e.parent.name + " - " + item.name;

                e.parent.children.push(item);
            }
            else {
                s.options.push(item);
            }

            return { hasChanged: true, options: s.options }
        });
    }

    private handleItemUpdate(e: TreeViewEvent) {
        if (!e.value || e.index === undefined) {
            return;
        }

        let item: ITreeViewItem;

        if (e.parent && e.parent.children) {
            // Inner item
            item = e.parent.children[e.index];
        }
        else {
            // Outer item
            item = this.state.options[e.index];
        }

        // item.id = e.parent.id + "_" + item.id;
        item.name = e.value;
        (item as any).shortName = e.shortValue;
        (item as any).fullName = e.parent ? (e.parent.name + " - " + item.name) : item.name;
        (item as any).cpt = e.extra.cpt || undefined;

        this.setState(s => ({
            options: s.options
        }));
    }

    private handleItemRemove(e: TreeViewEvent) {
        if (e.index === undefined) {
            return;
        }
        const index = e.index;

        this.setState(s => {
            if (e.parent) {
                if (e.parent.children) {
                    e.parent.children.splice(index, 1);
                }
            }
            else {
                s.options.splice(index, 1);
            }

            return { hasChanged: true, options: s.options }
        });
    }

    private handleDelete() {
        this.setState({
            isModalOpen: true
        });
    }

    private async doRealDelete() {
        try {
            this.setState({
                isModalOpen: false,
                submitStatus: true,
            });

            await V2.delete(`glossary/surgeries/${this.props.match.params.id}`);

            this.setState({
                hasChanged: false,
                submitStatus: false
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                submitStatus: e.message
            });
        }
    }

    private closeDialog() {
        this.setState({
            isModalOpen: false
        });
    }

    private renderOptionName(option: any) {
        return option.shortName ? `${option.name} (${option.shortName})` : option.name;
    }

    private comparisonToString(compare: number) {
        switch (compare) {
            case 0: return "Equals";
            case 1: return "Not equals";
            case 2: return "Greater than";
            case 3: return "Greater or equal";
            case 4: return "Less than";
            case 5: return "Less or equal";
            default: return (compare || "").toString();
        }
    }

    private functionToString(func: number) {
        switch (func) {
            case 0: return "Result";
            case 1: return "Set flag";
            case 2: return "Clear flag";
            default: return (func || "").toString();
        }
    }

    private moveInstructionUp(index: number) {
        if (index === 0) {
            return;
        }

        const instructions = this.state.instructions;
        const rows = instructions.splice(index, 1);
        instructions.splice(index - 1, 0, rows[0]);

        this.setState({
            instructions
        });
    }

    private moveInstructionDown(index: number) {
        if (index >= this.state.instructions.length - 1) {
            return;
        }

        const instructions = this.state.instructions;
        const rows = instructions.splice(index, 1);
        instructions.splice(index + 1, 0, rows[0]);

        this.setState({
            instructions
        });
    }

    private deleteRow(index: number) {
        if (index < 0 || index >= this.state.instructions.length) {
            return;
        }

        const instructions = this.state.instructions;
        instructions.splice(index, 1);

        this.setState({
            instructions
        });
    }

    private handleRowChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, index: number) {
        const name = e.currentTarget.name;
        let value: string | number = e.currentTarget.value;

        if (name === "function" || name === "compareValue" || name === "compareOperation") {
            value = parseInt(value, 10);
        }

        const instructions = this.state.instructions;
        instructions[index][name] = value;

        this.setState({
            instructions
        });
    }

    private toggleEdit(index: number) {
        const instructions = this.state.instructions;
        if (instructions[index].isEdit) {
            delete instructions[index].isEdit;
        }
        else {
            instructions[index].isEdit = true;
        }

        this.setState({
            instructions
        });
    }

    private addInstruction() {
        const instructions = this.state.instructions;

        instructions.push({ row: instructions.length, isEdit: true });

        this.setState({
            instructions
        });
    }
}

export default withTitle("Add/Edit Surgery Type", SurgeryTypeCreationView);
